import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getFoodTags, createFoodTag, toggleFoodTag, deleteFoodTag } from "../api/adminApi";
import {
  Typography, Button, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, TextField, DialogActions, Box, Chip, Stack,
  CircularProgress, TableContainer, useMediaQuery, useTheme, Card, CardContent,
  IconButton, DialogContentText
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";

import ScrollToTop from "../utils/ScrollToTop";

const modernInputStyle = {
  '& .MuiInputLabel-root': { color: '#A59D96', fontWeight: '600' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#D97706' },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px', bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: '#ECE5DA' },
    '&:hover fieldset': { borderColor: '#F29D17' },
    '&.Mui-focused fieldset': { borderColor: '#F8C332', borderWidth: '2px' }
  },
  '& .MuiOutlinedInput-input': { fontWeight: '700', color: '#2F2A25' }
};

export default function FoodTags({ zonalAdminId }) {
  const navigate = useNavigate();

  const { id: urlZonalAdminId } = useParams();
  const activeAdminId = zonalAdminId || urlZonalAdminId;

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);

  const [form, setForm] = useState({ name: "", sort_order: 0, image: null });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const params = activeAdminId ? { zonal_admin_id: activeAdminId } : {};
      const res = await getFoodTags(params);
      setTags(res.data);
    } catch (err) {
      alert("Failed to load tags");
    } finally {
      setLoading(false);
    }
  }, [activeAdminId]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("sort_order", form.sort_order);
      if (form.image) formData.append("image", form.image);

      if (activeAdminId) formData.append("zonal_admin_id", activeAdminId);

      await createFoodTag(formData);
      setOpen(false);
      setForm({ name: "", sort_order: 0, image: null });
      fetchTags();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create tag");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleFoodTag(id);
      fetchTags();
    } catch (err) {
      alert("Failed to update status");
    }
  };

  const handleDeleteClick = (tag) => {
    setTagToDelete(tag);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!tagToDelete) return;
    try {
      await deleteFoodTag(tagToDelete.id);
      fetchTags();
    } catch (err) {
      alert("Failed to delete tag");
    } finally {
      setDeleteDialogOpen(false);
      setTagToDelete(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1200, mx: "auto", minHeight: "100vh", bgcolor: "#FFFDF8", marginBottom: 15 }}>
      <ScrollToTop />

      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={2} mb={4}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: '#FFFFFF', border: '1px solid #ECE5DA', color: '#3A2C23', '&:hover': { bgcolor: '#F8C332', borderColor: '#F8C332' } }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", letterSpacing: "-0.5px" }}>
            Food Tags
          </Typography>
        </Box>

        <Button
          variant="contained" disableElevation onClick={() => setOpen(true)}
          sx={{
            borderRadius: '16px', textTransform: "none", fontWeight: 900, bgcolor: '#F8C332', color: '#3A2C23',
            boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)', '&:hover': { bgcolor: '#F5B71D' },
            py: { xs: 1.5, sm: 1.2 }, px: 3, width: { xs: "100%", sm: "auto" }
          }}
        >
          + Add New Tag
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} thickness={4} sx={{ color: '#F8C332' }} />
        </Box>
      ) : (
        <>
          {isMobile ? (
            <Stack spacing={2}>
              {tags?.map((t) => (
                <Card key={t.id} elevation={0} sx={{ borderRadius: '20px', border: "none", bgcolor: '#FFFFFF', boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)', transition: 'transform 0.2s ease, box-shadow 0.2s ease', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15)' } }}>
                  <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box flexShrink={0}>
                        {t.image_url ? (
                          <img src={t.image_url} alt={t.name} width={60} height={60} style={{ borderRadius: '12px', objectFit: "cover", boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }} />
                        ) : (
                          <Box width={60} height={60} bgcolor="#F2EEE8" borderRadius={2} display="flex" alignItems="center" justifyContent="center">
                            <Typography variant="caption" fontWeight="700" color="#A59D96">No Img</Typography>
                          </Box>
                        )}
                      </Box>
                      <Box flex={1}>
                        <Typography variant="subtitle1" fontWeight="900" color="#E85D3F" lineHeight={1.2} mb={0.5}>{t.name}</Typography>
                        <Typography variant="body2" color="#6E655E" fontWeight="600">Sort Order: <strong style={{ color: '#3A2C23' }}>{t.sort_order}</strong></Typography>
                      </Box>
                      <Box>
                        <Chip label={t.is_active ? "Active" : "Hidden"} color={t.is_active ? "success" : "default"} size="small" variant={t.is_active ? "filled" : "outlined"} sx={{ fontWeight: 800, borderRadius: '8px' }} />
                      </Box>
                    </Box>

                    <Box mt={2.5} display="flex" gap={1.5}>
                      <Button fullWidth size="small" variant={t.is_active ? "outlined" : "contained"} disableElevation onClick={() => handleToggle(t.id)} sx={{ borderRadius: '10px', fontWeight: 800, textTransform: 'none', py: 1, ...(t.is_active ? { color: '#6E655E', borderColor: '#ECE5DA', '&:hover': { bgcolor: '#FFF0EE', borderColor: '#E85D3F', color: '#E85D3F' } } : { bgcolor: '#DDECC8', color: '#577A34', '&:hover': { bgcolor: '#CCE3AF' } }) }}>
                        {t.is_active ? "Hide Tag" : "Show Tag"}
                      </Button>
                      <IconButton sx={{ color: '#E85D3F', bgcolor: '#FFF0EE', borderRadius: '10px', px: 2, '&:hover': { bgcolor: '#E85D3F', color: '#FFF' } }} onClick={() => handleDeleteClick(t)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}

              {tags.length === 0 && (
                <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: '20px', border: "1px dashed #ECE5DA", bgcolor: '#FFFFFF' }}>
                  <Typography color="text.secondary" fontWeight="700">No food tags found for this zone. Create one to get started!</Typography>
                </Paper>
              )}
            </Stack>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '20px', border: "none", bgcolor: '#FFFFFF', boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)', transition: 'transform 0.2s ease, box-shadow 0.2s ease', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15)' } }}>
              <Table sx={{ minWidth: 500 }}>
                <TableHead sx={{ bgcolor: "#FFFDF8" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Image</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Sort Order</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#A59D96' }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tags?.map((t) => (
                    <TableRow key={t.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell>
                        {t.image_url ? (
                          <img src={t.image_url} alt={t.name} width={50} height={50} style={{ borderRadius: '10px', objectFit: "cover", boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} />
                        ) : (
                          <Typography variant="body2" color="text.secondary" fontWeight="600">-</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 900, color: '#E85D3F' }}>{t.name}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#3A2C23' }}>{t.sort_order}</TableCell>
                      <TableCell>
                        <Chip label={t.is_active ? "Active" : "Hidden"} color={t.is_active ? "success" : "default"} size="small" variant={t.is_active ? "filled" : "outlined"} sx={{ fontWeight: 800, borderRadius: '6px' }} />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Button size="small" variant={t.is_active ? "outlined" : "contained"} disableElevation onClick={() => handleToggle(t.id)} sx={{ borderRadius: '8px', fontWeight: 800, textTransform: 'none', mr: 1.5, ...(t.is_active ? { color: '#6E655E', borderColor: '#ECE5DA', '&:hover': { bgcolor: '#FFF0EE', borderColor: '#E85D3F', color: '#E85D3F' } } : { bgcolor: '#DDECC8', color: '#577A34', '&:hover': { bgcolor: '#CCE3AF' } }) }}>
                          {t.is_active ? "Hide Tag" : "Show Tag"}
                        </Button>
                        <IconButton size="small" sx={{ color: '#E85D3F', bgcolor: '#FFF0EE', '&:hover': { bgcolor: '#E85D3F', color: '#FFF' } }} onClick={() => handleDeleteClick(t)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {tags.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary', fontWeight: 700 }}>No food tags found for this zone.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" fullScreen={fullScreenDialog} PaperProps={{ sx: { borderRadius: fullScreenDialog ? 0 : '24px', bgcolor: '#FFFDF8', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' } }}>
        <DialogTitle sx={{ fontWeight: 900, pb: 2, pt: 3, px: { xs: 2, sm: 4 }, color: '#3A2C23', borderBottom: '1px dashed #ECE5DA' }}>Create Food Tag</DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 4 }, pt: "24px !important", pb: { xs: '15vh', sm: 4 } }}>
          <Stack spacing={2.5}>
            <TextField sx={modernInputStyle} label="Tag Name (e.g. Signature Biryani)" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField sx={modernInputStyle} label="Sort Order (0 comes first)" type="number" fullWidth value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
            <Box sx={{ border: '1px dashed #ECE5DA', borderRadius: '12px', p: 3, bgcolor: '#FFFFFF', textAlign: 'center' }}>
              <Typography variant="body2" mb={1.5} fontWeight="700" color="#3A2C23">Upload Circular Image</Typography>
              <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, image: e.target.files[0] })} style={{ fontFamily: 'inherit', fontSize: '0.85rem', color: '#6E655E', cursor: 'pointer' }} />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, px: { sm: 4 }, borderTop: "1px solid #ECE5DA", bgcolor: '#FFFFFF', borderRadius: '0 0 24px 24px' }}>
          <Button onClick={() => setOpen(false)} color="inherit" sx={{ textTransform: "none", fontWeight: 800, color: '#6E655E', borderRadius: '12px', px: 3 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting} disableElevation sx={{ textTransform: "none", fontWeight: 900, borderRadius: '12px', bgcolor: '#F8C332', color: '#3A2C23', px: 4, py: 1.2, boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(248, 195, 50, 0.3)', '&:hover': { bgcolor: '#F5B71D' } }}>
            {isSubmitting ? <CircularProgress size={24} sx={{ color: '#3A2C23' }} /> : "Save Tag"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px', bgcolor: '#FFFDF8' } }}>
        <DialogTitle sx={{ fontWeight: 900, color: '#3A2C23' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#6E655E", fontWeight: 600 }}>Are you sure you want to delete the tag <Typography component="span" fontWeight="900" color="#E85D3F">{tagToDelete?.name}</Typography>?</DialogContentText>
          <DialogContentText sx={{ color: "#DC2626", mt: 2, fontSize: "0.875rem", fontWeight: 700 }}>This action is permanent and cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontWeight: 800, color: '#6E655E', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" disableElevation sx={{ fontWeight: 800, borderRadius: '10px', textTransform: 'none', bgcolor: '#DC2626', color: '#FFFFFF', '&:hover': { bgcolor: '#B91C1C' } }}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}